import React from "react";
import { useNavigate } from "react-router-dom";
import SiteNav from "@/components/SiteNav";
import SiteFooter from "@/components/SiteFooter";
import { PageHero, CTASection } from "@/components/SitePage";
import { useLang } from "@/i18n";
import { CheckCircle2, ArrowRight } from "lucide-react";

const RED = "#c00000";
const O = { fontFamily: "Outfit, sans-serif" };

export default function Preise() {
  const navigate = useNavigate();
  const { lang } = useLang();
  const de = lang === "de";
  const t = (d, e) => (de ? d : e);

  const TIERS = [
    {
      name: "Starter", price: t("0", "0"), unit: t("kostenlos", "free"), tag: t("Zum Ausprobieren", "To try out"),
      desc: t("Ideal für kleine Teams und den ersten Überblick.", "Ideal for small teams and a first overview."),
      features: [t("Bis 25 Assets", "Up to 25 assets"), t("Live-Status & Ampel", "Live status & traffic light"), t("1 Standort", "1 location"), t("E-Mail-Support", "Email support")],
      cta: t("Kostenlos testen", "Try for free"), highlight: false,
    },
    {
      name: "Professional", price: t("auf Anfrage", "on request"), unit: t("pro Monat", "per month"), tag: t("Beliebt", "Popular"),
      desc: t("Für wachsende Flotten über mehrere Standorte.", "For growing fleets across multiple locations."),
      features: [t("Unbegrenzte Assets", "Unlimited assets"), t("Tickets & Rollout", "Tickets & rollout"), t("Mehrere Standorte", "Multiple locations"), t("TeamViewer-Fernzugriff", "TeamViewer remote access"), t("Berichte & Export", "Reports & export"), t("Prioritäts-Support", "Priority support")],
      cta: t("Demo anfragen", "Request demo"), highlight: true,
    },
    {
      name: "Enterprise", price: t("individuell", "custom"), unit: t("maßgeschneidert", "tailored"), tag: t("Für Konzerne", "For enterprises"),
      desc: t("Mandantenfähig für viele Kunden und Standorte.", "Multi-tenant for many customers and locations."),
      features: [t("Alles aus Professional", "Everything in Professional"), t("Mandanten & Kundenportale", "Tenants & customer portals"), t("Rollen & Rechte fein steuerbar", "Fine-grained roles & permissions"), t("SLA & dedizierter Ansprechpartner", "SLA & dedicated contact"), t("Individuelle Integrationen", "Custom integrations")],
      cta: t("Kontakt aufnehmen", "Get in touch"), highlight: false,
    },
  ];
  const FAQ = [
    [t("Wie wird abgerechnet?", "How is it billed?"), t("Transparent pro Monat nach Umfang – ohne versteckte Kosten. Enterprise wird individuell kalkuliert.", "Transparently per month by scope – no hidden costs. Enterprise is quoted individually.")],
    [t("Gibt es eine Mindestlaufzeit?", "Is there a minimum term?"), t("Nein. Sie können flexibel starten; für Enterprise vereinbaren wir passende Konditionen.", "No. You can start flexibly; for Enterprise we agree suitable terms.")],
    [t("Kann ich später wechseln?", "Can I switch later?"), t("Jederzeit. Ein Upgrade ist ohne Datenverlust und ohne Ausfallzeit möglich.", "Anytime. Upgrading works without data loss or downtime.")],
    [t("Sind meine Daten sicher?", "Is my data safe?"), t("Alle Daten werden DSGVO-konform in Deutschland verarbeitet und verschlüsselt übertragen.", "All data is processed GDPR-compliant in Germany and transferred encrypted.")],
  ];

  return (
    <div className="min-h-screen bg-white text-gray-900" style={{ fontFamily: "Inter, sans-serif" }}>
      <SiteNav />
      <PageHero eyebrow={t("PREISE", "PRICING")} title={t("Faire Preise,", "Fair pricing,")} highlight={t("volle Kontrolle", "full control")} subtitle={t("Wählen Sie das Paket, das zu Ihrer Flotte passt – und wachsen Sie jederzeit mit. Unverbindlich starten, flexibel skalieren.", "Choose the plan that fits your fleet – and scale anytime. Start with no obligation, scale flexibly.")} />

      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-16">
        <div className="grid md:grid-cols-3 gap-6 items-stretch">
          {TIERS.map((tier) => (
            <div key={tier.name} data-testid={`preis-${tier.name.toLowerCase()}`}
              className={`relative flex flex-col rounded-2xl p-7 transition-all hover:-translate-y-1 ${tier.highlight ? "text-white shadow-2xl" : "bg-white border border-gray-200 shadow-sm hover:shadow-xl hover:border-[#c00000]"}`}
              style={tier.highlight ? { background: "linear-gradient(160deg,#1B1E23,#23272E)" } : undefined}>
              {tier.highlight && <div aria-hidden className="absolute inset-0 rounded-2xl pointer-events-none" style={{ background: "radial-gradient(120% 80% at 100% 0%, rgba(192,0,0,0.3), transparent 55%)" }} />}
              <div className="relative">
                <div className="flex items-center justify-between">
                  <div className="text-sm font-bold" style={O}>{tier.name}</div>
                  <span className="text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full" style={{ background: tier.highlight ? RED : `${RED}12`, color: tier.highlight ? "#fff" : RED }}>{tier.tag}</span>
                </div>
                <div className="mt-5 flex items-baseline gap-2">
                  <span className="text-4xl font-extrabold" style={O}>{tier.price}</span>
                  <span className={`text-sm ${tier.highlight ? "text-gray-300" : "text-gray-500"}`}>{tier.unit}</span>
                </div>
                <p className={`mt-3 text-sm leading-relaxed ${tier.highlight ? "text-gray-300" : "text-gray-600"}`}>{tier.desc}</p>
                <ul className="mt-6 space-y-2.5">
                  {tier.features.map((f) => (
                    <li key={f} className={`flex items-start gap-2 text-sm ${tier.highlight ? "text-gray-200" : "text-gray-700"}`}>
                      <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" style={{ color: tier.highlight ? "#fff" : RED }} /> {f}
                    </li>
                  ))}
                </ul>
              </div>
              <button onClick={() => navigate("/kontakt")} data-testid={`preis-cta-${tier.name.toLowerCase()}`}
                className="relative mt-8 inline-flex items-center justify-center gap-2 rounded-xl px-5 py-3 text-sm font-bold text-white transition-all hover:brightness-110"
                style={{ background: RED }}>
                {tier.cta} <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
        <p className="text-center text-[11px] text-gray-400 mt-6">{t("Alle Preise zzgl. MwSt. · Illustrative Pakete · Details im persönlichen Gespräch.", "All prices excl. VAT · Illustrative plans · Details in a personal conversation.")}</p>
      </section>

      <section className="bg-[#f7f8fa] border-y border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16 grid lg:grid-cols-3 gap-10">
          <div>
            <div className="text-xs font-bold tracking-[0.2em] text-gray-400 mb-3" style={O}>{t("FRAGEN ZU PREISEN", "PRICING QUESTIONS")}</div>
            <h2 className="text-2xl font-extrabold tracking-tight" style={O}>{t("Alles Wichtige auf einen Blick", "Everything important at a glance")}</h2>
          </div>
          <div className="lg:col-span-2 divide-y divide-gray-200">
            {FAQ.map(([q, a]) => (
              <div key={q} className="py-5">
                <div className="text-sm font-bold" style={O}>{q}</div>
                <div className="text-sm text-gray-600 mt-1.5 leading-relaxed">{a}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <CTASection title={t("Noch unsicher, welches Paket passt?", "Not sure which plan fits?")} text={t("Wir beraten Sie unverbindlich und finden gemeinsam die richtige Lösung.", "We'll advise you with no obligation and find the right solution together.")} primary={t("Beratung anfragen", "Request consultation")} />
      <SiteFooter />
    </div>
  );
}
